import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API_CONFIG from '../config/api';
import { createAdminSession } from '../utils/sessionManager';
import { useBranding, useDocumentBranding, templateStyle, normalizeTemplate } from '../utils/loginBranding';
import { LOGIN_LAYOUTS, PassbookLayout } from './loginLayouts';
import './AdminLoginPage.css';
import './AdminLoginTemplates.css';

function AdminLoginPage() {
    const navigate = useNavigate();
    // Logo, names, wording and accent come from Settings → Login Page Branding.
    const brand = useBranding();
    const emailPlaceholder = `you@${brand.emailDomain}`;
    const [loginMode, setLoginMode] = useState('email');

    // Email login states
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    // Mobile OTP login states — the backend looks the account up by phone
    // alone (see /api/admin/request-otp), so no separate email field here.
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState(['', '', '', '']); // 4-digit OTP array
    const [otpSent, setOtpSent] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);

    // Common states
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useDocumentBranding(brand, `${brand.brandName} Admin Portal`);

    // OTP countdown timer
    useEffect(() => {
        if (otpTimer > 0) {
            const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [otpTimer]);

    const switchMode = (mode) => {
        setLoginMode(mode);
        setError('');
        // Mobile OTP and Email OTP share the same otp/otpSent/otpTimer state
        // (only one identifier flow is ever active) — reset it on every mode
        // switch so a code requested for the old identifier can't be verified
        // as if it belonged to the new one.
        setOtpSent(false);
        setOtp(['', '', '', '']);
        setOtpTimer(0);
    };

    const handleOtpChange = (index, value) => {
        if (value.length <= 1 && /^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Auto-focus next input
            if (value && index < 3) {
                const nextInput = document.getElementById(`otp-${index + 1}`);
                if (nextInput) nextInput.focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            if (prevInput) prevInput.focus();
        }
    };

    // Step 1 of email login: verify the password. The backend never returns
    // a session from this call any more — a correct password gets an OTP
    // emailed to the account (two-factor), and the session is only created
    // once handleVerifyOTP confirms that code. Also doubles as "Resend Code"
    // for this mode, since a fresh OTP still requires the password to be
    // right — see the button below.
    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError('Please enter your credentials');
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                setIsLoading(false);
                setError(data.message || 'Authentication failed');
                return;
            }

            setOtpSent(true);
            setOtpTimer(300); // 5 minutes
            setIsLoading(false);
            setError('');

            if (data.devOtp) {
                console.log('🛠️ DEV OTP:', data.devOtp);
            }

        } catch (error) {
            console.error('❌ Login error:', error);
            setIsLoading(false);
            setError('Server connection failed. Please check your network.');
        }
    };

    // Mobile OTP's "request a code" step — phone-only, no password concept.
    const handleRequestOTP = async (e) => {
        e.preventDefault();
        setError('');

        if (!phone || phone.length !== 10) {
            setError('Please enter a valid 10-digit mobile number');
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/request-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phone })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                setIsLoading(false);
                setError(data.message || 'Account not found or access denied');
                return;
            }

            setOtpSent(true);
            setOtpTimer(300); // 5 minutes
            setIsLoading(false);
            setError('');

            if (data.devOtp) {
                console.log('🛠️ DEV OTP:', data.devOtp);
            }

        } catch (error) {
            console.error('❌ OTP Request error:', error);
            setIsLoading(false);
            setError('Server connection failed. Please try again later.');
        }
    };

    // Verifies the code for whichever flow sent one: the email+password 2FA
    // step (loginMode === 'email', identified by the same email field the
    // password form used) or the standalone Mobile OTP flow (by phone).
    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setError('');

        const otpValue = otp.join('');
        if (otpValue.length !== 4) {
            setError('Please enter the complete 4-digit code');
            return;
        }

        setIsLoading(true);

        try {
            const isEmailMode = loginMode === 'email';
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/verify-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(isEmailMode ? { email, otp: otpValue } : { phone, otp: otpValue })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                setIsLoading(false);
                setError(data.message || 'The code you entered is incorrect');
                return;
            }

            createAdminSession(data.data, rememberMe);
            setIsLoading(false);

            if (data.data.role === 'super_admin') {
                navigate('/admin/super-dashboard', { replace: true });
            } else {
                navigate('/admin/super-dashboard', { replace: true });
            }

        } catch (error) {
            console.error('❌ OTP Verification error:', error);
            setIsLoading(false);
            setError('Verification failed. Server connection error.');
        }
    };

    const resetOTPFlow = () => {
        setOtpSent(false);
        setOtp(['', '', '', '']);
        setOtpTimer(0);
        setError('');
    };

    // Which of the five layouts this brand picked (Settings → Branding →
    // Login page design); the layout only arranges the brand around the
    // panel below, which is the same in all of them.
    const Layout = LOGIN_LAYOUTS[normalizeTemplate(brand.template)] || PassbookLayout;
    const rootStyle = templateStyle(brand.template, brand.accentColor, { pageColor: brand.pageColor, cardColor: brand.cardColor });

    return (
        <Layout brand={brand} style={rootStyle}>
            <div className="login-mode-tabs">
                <button
                    type="button"
                    className={`mode-tab ${loginMode === 'email' ? 'active' : ''}`}
                    onClick={() => switchMode('email')}
                >
                    Email Login
                </button>
                <button
                    type="button"
                    className={`mode-tab ${loginMode === 'mobile' ? 'active' : ''}`}
                    onClick={() => switchMode('mobile')}
                >
                    Mobile OTP
                </button>
            </div>

            {error && (
                <div className="admin-alert">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{error}</span>
                </div>
            )}

            {loginMode === 'email' && !otpSent && (
                <form onSubmit={handleEmailLogin} className="admin-form">
                    <div className="admin-input-group">
                        <label htmlFor="email">Email</label>
                        <div className="input-with-icon">
                            <div className="input-icon">
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                            </div>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={emailPlaceholder}
                                disabled={isLoading}
                                required
                            />
                        </div>
                    </div>

                    <div className="admin-input-group">
                        <label htmlFor="password">Password</label>
                        <div className="input-with-icon">
                            <div className="input-icon">
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                            </div>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                disabled={isLoading}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={isLoading}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? (
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                    </svg>
                                ) : (
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                        <circle cx="12" cy="12" r="3"></circle>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="admin-form-options">
                        <label className="remember-me">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                disabled={isLoading}
                            />
                            <span>Remember me</span>
                        </label>
                        <Link to="/admin/forgot-password" className="forgot-btn">
                            Forgot password?
                        </Link>
                    </div>

                    <button
                        type="submit"
                        className="admin-submit-btn"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className="btn-content">
                                <div className="admin-spinner"></div>
                                <span>Signing in…</span>
                            </div>
                        ) : (
                            <div className="btn-content">
                                <span>Sign in</span>
                            </div>
                        )}
                    </button>
                </form>
            )}

            {loginMode === 'email' && otpSent && (
                <form onSubmit={handleVerifyOTP} className="admin-form">
                    <div className="otp-sent-message">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <p>Password verified — OTP sent to {email}</p>
                    </div>

                    <div className="admin-input-group">
                        <label>Verification Code</label>
                        <div className="otp-digit-container">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    id={`otp-${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength="1"
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    className="otp-digit-input"
                                    disabled={isLoading}
                                    autoFocus={index === 0}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="otp-actions">
                        <button
                            type="button"
                            className="change-number-btn"
                            onClick={resetOTPFlow}
                            disabled={isLoading}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <path d="M19 12H5M12 19l-7-7 7-7"></path>
                            </svg>
                            Back
                        </button>
                        {otpTimer === 0 ? (
                            <button
                                type="button"
                                className="resend-otp-link"
                                onClick={handleEmailLogin}
                                disabled={isLoading}
                            >
                                Resend Code
                            </button>
                        ) : (
                            <span className="otp-timer">
                                Resend in {Math.floor(otpTimer / 60)}:{(otpTimer % 60).toString().padStart(2, '0')}
                            </span>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="admin-submit-btn"
                        disabled={isLoading || otp.join('').length !== 4}
                    >
                        {isLoading ? (
                            <div className="btn-content">
                                <div className="admin-spinner"></div>
                                <span>Verifying…</span>
                            </div>
                        ) : (
                            <div className="btn-content">
                                <span>Verify code</span>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                                </svg>
                            </div>
                        )}
                    </button>
                </form>
            )}

            {loginMode === 'mobile' && !otpSent && (
                <form onSubmit={handleRequestOTP} className="admin-form">
                    <div className="admin-input-group">
                        <label htmlFor="phone">Mobile Number</label>
                        <div className="input-with-icon">
                            <div className="input-icon">
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                </svg>
                            </div>
                            <input
                                type="tel"
                                id="phone"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                placeholder="10-digit number"
                                disabled={isLoading}
                                maxLength="10"
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="admin-submit-btn"
                        disabled={isLoading || phone.length !== 10}
                    >
                        {isLoading ? (
                            <div className="btn-content">
                                <div className="admin-spinner"></div>
                                <span>Sending code…</span>
                            </div>
                        ) : (
                            <div className="btn-content">
                                <span>Request OTP</span>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </div>
                        )}
                    </button>
                </form>
            )}

            {loginMode === 'mobile' && otpSent && (
                <form onSubmit={handleVerifyOTP} className="admin-form">
                    <div className="otp-sent-message">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <p>OTP sent to +91 {phone}</p>
                    </div>

                    <div className="admin-input-group">
                        <label>Verification Code</label>
                        <div className="otp-digit-container">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    id={`otp-${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength="1"
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    className="otp-digit-input"
                                    disabled={isLoading}
                                    autoFocus={index === 0}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="otp-actions">
                        <button
                            type="button"
                            className="change-number-btn"
                            onClick={resetOTPFlow}
                            disabled={isLoading}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <path d="M19 12H5M12 19l-7-7 7-7"></path>
                            </svg>
                            Back
                        </button>
                        {otpTimer === 0 ? (
                            <button
                                type="button"
                                className="resend-otp-link"
                                onClick={handleRequestOTP}
                                disabled={isLoading}
                            >
                                Resend Code
                            </button>
                        ) : (
                            <span className="otp-timer">
                                Resend in {Math.floor(otpTimer / 60)}:{(otpTimer % 60).toString().padStart(2, '0')}
                            </span>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="admin-submit-btn"
                        disabled={isLoading || otp.join('').length !== 4}
                    >
                        {isLoading ? (
                            <div className="btn-content">
                                <div className="admin-spinner"></div>
                                <span>Verifying…</span>
                            </div>
                        ) : (
                            <div className="btn-content">
                                <span>Verify code</span>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                                </svg>
                            </div>
                        )}
                    </button>
                </form>
            )}

            <div className="admin-card-footer">
                <div className="security-tag">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    Encrypted connection
                </div>
            </div>
        </Layout>
    );
}

export default AdminLoginPage;
